import React, { useState } from 'react';
import {
  Users,
  UserPlus,
  Shield,
  Crown,
  X,
  Check,
  Search,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArenaCohost, useArenaCohosts } from '@/hooks/useArenaCohosts';
import { ArenaParticipant } from '@/hooks/useLiveArenas';
import { toast } from '@/hooks/use-toast';

interface CohostManagementPanelProps {
  arenaId: string;
  isHost: boolean;
  participants: ArenaParticipant[];
  hostId: string;
}

export const CohostManagementPanel: React.FC<CohostManagementPanelProps> = ({
  arenaId,
  isHost,
  participants,
  hostId,
}) => {
  const { cohosts, pendingInvites, inviteCohost, removeCohost, loading } =
    useArenaCohosts(arenaId);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter participants who can be invited (not host, not already cohost/pending)
  const invitableParticipants = participants.filter((p) => {
    if (p.user_id === hostId) return false;
    if (cohosts.some((c) => c.user_id === p.user_id)) return false;
    if (pendingInvites.some((c) => c.user_id === p.user_id)) return false;
    if (!searchQuery) return true;
    return p.profile?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleInvite = async (userId: string) => {
    await inviteCohost(userId);
  };

  const handleRemove = async (cohost: ArenaCohost) => {
    await removeCohost(cohost.id);
  };

  if (!isHost) return null;

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Shield className="w-4 h-4" />
          Co-hosts
          {cohosts.length > 0 && (
            <Badge variant="secondary" className="ml-1">
              {cohosts.length}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Co-host Management
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Active Cohosts */}
          <div>
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" />
              Active Co-hosts ({cohosts.length})
            </h3>
            {cohosts.length === 0 ? (
              <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-4">
                No co-hosts yet. Invite participants to help moderate!
              </p>
            ) : (
              <div className="space-y-2">
                {cohosts.map((cohost) => (
                  <div
                    key={cohost.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={cohost.profile?.photo_url || ''} />
                        <AvatarFallback>
                          {cohost.profile?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{cohost.profile?.name}</p>
                        <p className="text-xs text-muted-foreground">Co-host</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleRemove(cohost)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending Invites */}
          {pendingInvites.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Pending Invites ({pendingInvites.length})
              </h3>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/20"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={invite.profile?.photo_url || ''} />
                        <AvatarFallback>
                          {invite.profile?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{invite.profile?.name}</p>
                        <p className="text-xs text-amber-600">Pending...</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleRemove(invite)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite Participants */}
          <div>
            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-primary" />
              Invite Participants
            </h3>

            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search participants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[200px]">
              {invitableParticipants.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No participants available to invite
                </p>
              ) : (
                <div className="space-y-2">
                  {invitableParticipants.map((participant) => (
                    <div
                      key={participant.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage
                            src={participant.profile?.photo_url || ''}
                          />
                          <AvatarFallback>
                            {participant.profile?.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {participant.profile?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {participant.is_speaker ? 'Speaker' : 'Listener'}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={loading}
                        onClick={() => handleInvite(participant.user_id)}
                        className="gap-1"
                      >
                        <UserPlus className="w-3 h-3" />
                        Invite
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Info */}
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              Co-host Privileges
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Mute/unmute participants</li>
              <li>• Remove disruptive users</li>
              <li>• Spotlight speakers</li>
              <li>• Pin important messages</li>
              <li>• Promote hand-raisers to speakers</li>
            </ul>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CohostManagementPanel;
